/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { TrackedObject } from 'tracked-built-ins';
import diff from 'microdiff';
import { trackedTask } from 'reactiveweb/ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import { keepLatestTask, dropTask } from 'ember-concurrency';
import ClientsQuery from 'my-phorest/gql/queries/clients.graphql';

export default class AiSettingsSlideOverComponent extends Component {
  @service twoWayChat;
  @queryManager apollo;

  @tracked currentView = 'settings';
  @tracked settings;
  @tracked originalBranchAiSettings;

  constructor() {
    super(...arguments);
    this.loadSettingsTask.perform();
  }

  @dropTask
  *loadSettingsTask() {
    const loadedSettings =
      yield this.twoWayChat.getBranchAiSettingsTask.perform();

    this.originalBranchAiSettings = loadedSettings;

    // Initialize working copy
    this.settings = new TrackedObject({
      aiEnablementMode: loadedSettings?.aiEnablementMode || 'OFF',
      testClientIds: [...(loadedSettings?.testClientIds || [])],
    });
  }

  fetchTestClientsTaskInstance = trackedTask(
    this,
    this.fetchTestClientsTask,
    () => [this.settings?.aiEnablementMode, this.settings?.testClientIds]
  );

  get selectedTestClients() {
    return this.fetchTestClientsTaskInstance.value || [];
  }

  @keepLatestTask
  *fetchTestClientsTask() {
    if (
      this.settings?.aiEnablementMode !== 'TEST' ||
      !this.settings?.testClientIds ||
      this.settings.testClientIds.length === 0
    ) {
      return [];
    }

    try {
      const result = yield this.apollo.query(
        {
          query: ClientsQuery,
          variables: {
            filterBy: {
              ids: this.settings.testClientIds,
            },
          },
        },
        'clients'
      );

      const clients = result?.edges?.map((edge) => edge.node) || [];

      // Sort clients to match the order in testClientIds
      return clients.sort((a, b) => {
        const indexA = this.settings.testClientIds.indexOf(a.id);
        const indexB = this.settings.testClientIds.indexOf(b.id);
        return indexA - indexB;
      });
    } catch (error) {
      console.error('Failed to fetch test clients:', error);
      return [];
    }
  }

  #getEditableProps(obj) {
    return {
      aiEnablementMode: obj?.aiEnablementMode || 'OFF',
      testClientIds: obj?.testClientIds || [],
    };
  }

  get hasUnsavedChanges() {
    if (!this.settings || !this.originalBranchAiSettings) {
      return false;
    }

    return !!diff(
      this.#getEditableProps(this.settings),
      this.#getEditableProps(this.originalBranchAiSettings)
    ).length;
  }

  get isTestModeWithoutClient() {
    return (
      this.settings?.aiEnablementMode === 'TEST' &&
      (!this.settings?.testClientIds ||
        this.settings.testClientIds.length === 0)
    );
  }

  get isSaveDisabled() {
    if (this.args.isSaving) return true;
    if (!this.hasUnsavedChanges) return true;
    if (this.isTestModeWithoutClient) return true;
    return false;
  }

  @action
  handleModeChange(newMode) {
    this.settings = new TrackedObject({
      ...this.settings,
      aiEnablementMode: newMode,
    });
  }

  @action
  showClientSearch() {
    this.currentView = 'client-search';
  }

  @action
  navigateBackToSettings() {
    this.currentView = 'settings';
  }

  @action
  async handleClientSelect(client) {
    const currentIds = this.settings.testClientIds || [];
    this.settings = new TrackedObject({
      ...this.settings,
      testClientIds: [...currentIds, client.id],
    });

    this.currentView = 'settings';
  }

  @action
  handleRemoveTester(clientId) {
    const currentIds = this.settings.testClientIds || [];
    this.settings = new TrackedObject({
      ...this.settings,
      testClientIds: currentIds.filter((id) => id !== clientId),
    });
  }

  @action
  async handleSave() {
    await this.args.onSave(this.settings);
  }

  @action
  handleClose() {
    this.currentView = 'settings';
    this.args.onClose();
  }
}
